import type * as React from 'react';
import type { DescribeTemplateResult, TemplateReport } from '@verclam/docx-kit-template-engine';
import documentTemplateFileDescribed, {
	type DeclaredFieldPayload,
	type DeclaredNodePayload,
	type DocumentTemplateFileDescribedAction,
	type DocumentTemplateFileDescribedPayload,
} from '@Application/Documents/TemplateFiles/Actions/documentTemplateFileDescribed';
import documentTemplateFileDescriptionFailed, {
	type DocumentTemplateFileDescriptionFailedAction,
} from '@Application/Documents/TemplateFiles/Actions/documentTemplateFileDescriptionFailed';
import type DescribeDocumentTemplateFile from '@Application/Documents/TemplateFiles/Queries/DescribeDocumentTemplateFile';
import ensureDocxKitModules from '@Application/Documents/DocxKit/ensureDocxKitModules';
import extractMessages, { type ExtractedMessages } from '@Infra/FetchLib/ApiError/extractMessages';
import { describeTemplate } from '@verclam/docx-kit-template-engine';

const UNREADABLE_PACKAGE_ISSUE_CODE = 'template.package.unreadable';
const MALFORMED_CATALOG_ISSUE_CODE_PREFIX = 'catalog.';

type Actions = DocumentTemplateFileDescribedAction | DocumentTemplateFileDescriptionFailedAction;

const hasIssueWithCode = (report: TemplateReport, code: string): boolean => {
	return report.issues.some((issue) => issue.code === code);
};

const hasMalformedCatalogIssue = (report: TemplateReport): boolean => {
	return report.issues.some(
		(issue) => issue.severity === 'error' && issue.code.startsWith(MALFORMED_CATALOG_ISSUE_CODE_PREFIX),
	);
};

const compareByOrderThenPath = (
	left: { order: number; path: string },
	right: { order: number; path: string },
): number => {
	return left.order !== right.order ? left.order - right.order : left.path.localeCompare(right.path);
};

/**
 * Mirrors the server's decision rule (`DocumentTemplateDeclarationReader`, back): unreadable on
 * `template.package.unreadable`; with no description, malformed when the report also carries a
 * `catalog.*` error, undeclared otherwise; declared in every other case, regardless of any other
 * anomaly — anomalies are never surfaced to the screen.
 */
const resolvePayload = ({
	description,
	report,
}: DescribeTemplateResult): DocumentTemplateFileDescribedPayload => {
	if (hasIssueWithCode(report, UNREADABLE_PACKAGE_ISSUE_CODE)) {
		return { status: 'unreadable' };
	}

	if (description === undefined) {
		return hasMalformedCatalogIssue(report) ? { status: 'malformed' } : { status: 'undeclared' };
	}

	const { catalog } = description;

	const nodes: DeclaredNodePayload[] = [...catalog.nodes].sort(compareByOrderThenPath).map((node) => ({
		uuid: node.uuid,
		path: node.path,
		name: node.name,
		isList: node.kind === 'list',
		order: node.order,
	}));

	const fields: DeclaredFieldPayload[] = [...catalog.fields].sort(compareByOrderThenPath).map((field) => ({
		uuid: field.uuid,
		path: field.path,
		label: field.label,
		type: field.type,
		isRequired: field.facets?.required === true,
		order: field.order,
	}));

	return { status: 'declared', fieldCount: catalog.fields.length, nodes, fields };
};

// `extractMessages` only recognizes `ApiError`: a failed file read never is one, so it always returns
// `undefined` here. The fallback keeps the failure action's message non-empty without a cast.
const toExtractedMessages = (error: unknown): ExtractedMessages => {
	return extractMessages(error) ?? { message: "Le fichier n'a pas pu être lu.", fieldErrors: [] };
};

/**
 * Describes an uploaded file for the deposit sidebar's local preview. The engine's WebAssembly modules
 * are loaded before the first call.
 */
export default (query: DescribeDocumentTemplateFile) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			await ensureDocxKitModules();
			const buffer = await query.file.arrayBuffer();

			const result = describeTemplate(new Uint8Array(buffer));
			const action = documentTemplateFileDescribed(resolvePayload(result));
			dispatch(action);
			return action;
		} catch (error) {
			const action = documentTemplateFileDescriptionFailed(toExtractedMessages(error));
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
